/**
 * Server administration: accounts, aliases, and the log of every change.
 *
 * THE DIFF IS THE PRODUCT, and this component's whole job is to put one in front
 * of somebody before anything happens. ops.md §5 requires it — "the UI shows the
 * diff before the apply button lights" — and the reason is that a confirmation
 * saying "this will create an alias" is a CLAIM, while a diff is the change
 * itself. A form that summarised what it was about to do would be asking
 * somebody to trust the summary.
 *
 * IT DECIDES NOTHING. Every refusal here is one the warden makes: which verbs
 * exist, whether a re-auth is needed, whether the tree moved under a plan
 * somebody was reading. The client's job is to show the diff and the reason, and
 * to be honest about what it does not know.
 *
 * THE LOG IS ALSO WHERE A CHANGE IS UNDONE, and undoing one is a verb rather
 * than a button with its own route: it is planned, diffed and applied down the
 * path everything else uses, so what somebody approves is the diff of the undo
 * itself. The warden decides what an undo means — one action is several commits
 * — and this offers it only on the rows the warden marks.
 *
 * ONLY RENDERED WITH `admin.server`, which is tailnet-only — so the same person
 * has no admin surface at all on the public path. That is not a control this
 * component enforces; the origin refuses either way. It is here so the surface
 * is not offered where it cannot work.
 *
 * AND IT IS WHERE THE SECOND FACTOR IS SPENT. `account.delete`, `master.rotate`
 * and forwarding outside the household need a sign-in from the last fifteen
 * minutes (ops.md §5), which cannot be the request's own credential: this panel
 * reaches the warden over the tailnet, where a request carries a Tailscale
 * identity and no Access token at all. `apps/mailgw/src/auth/reauth.ts` sets
 * that argument out in full. So the ceremony is manual and it is deliberately
 * manual — open the Access-protected page, sign in, bring the token back — and
 * the panel's whole job is to be the place it is brought back to. Until it was,
 * `api.reauth` had no caller and those three verbs were unreachable by anybody.
 */
import { useCallback, useEffect, useState } from 'react'
import { api, HearthError, type WardenAudit, type WardenPlan } from './api.ts'
import { ReauthForm } from './ReauthForm.tsx'
import { ServerControls } from './ServerControls.tsx'

export interface AdminProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly onError: (message: string) => void
  /**
   * What the origin says this principal may do, from `/whoami`.
   *
   * THE THREE ADMIN CAPABILITIES ARE THREE DIFFERENT POWERS, and this panel used
   * to treat them as one. `admin.accounts` gates account administration,
   * `admin.logs` gates the audit trail, and `admin.server` gates everything else
   * including `master.rotate` — separable at the origin since the verb table
   * landed, and until now not separable here: the whole screen appeared only for
   * `admin.server`, so granting somebody `admin.logs` alone gave them a
   * capability that reached no screen at all. Two predicates about one screen.
   *
   * ABSENT MEANS SHOW EVERYTHING, which is the same rule the rest of the client
   * follows: `/whoami` is a second request, and a flaky network must not paint a
   * reader as holding no permissions. The origin refuses what it refuses.
   */
  readonly capabilities?: ReadonlySet<string>
}

type Tab = 'accounts' | 'aliases' | 'disposable' | 'maintenance' | 'audit'

/**
 * Which capability each tab is, which is the origin's own table read backwards.
 *
 * `WARDEN_VERB_CAPABILITY` in `apps/mailgw/src/api/app.ts` maps `account.*` to
 * `admin.accounts` and everything else to `admin.server`, and the audit route
 * takes `admin.logs`. This is that, grouped by the tab the verbs live on — a
 * copy across a workspace boundary, like `SNOOZE_SPECIAL_USE` and `REAUTH_PATH`
 * beside it, and one whose drift costs a tab that 403s rather than anything
 * dangerous: the origin refuses regardless of what this says.
 */
const TAB_CAPABILITY: Readonly<Record<Tab, string>> = {
  accounts: 'admin.accounts',
  aliases: 'admin.server',
  disposable: 'admin.server',
  // THE CONTAINER ROUTE IS `admin.server` WHATEVER THE VERB: `/admin/warden/ctl`
  // is gated by its own `require()`, not by the verb table — so
  // `account.purgeMaildir` lives here and not under `admin.accounts`, where its
  // name would put it.
  maintenance: 'admin.server',
  audit: 'admin.logs',
}

/**
 * One row of the log, with the flag the warden puts on it.
 *
 * `revertable` IS READ AS OPTIONAL because `WardenAudit` does not declare it: an
 * origin that has not learned to say it yet answers rows without the field, and
 * the missing value must mean "no button" rather than an undo offered on
 * something the warden would refuse.
 */
type AuditRow = WardenAudit & { readonly revertable?: boolean }

/**
 * The address a mint created, read the same way and for the same reason.
 *
 * `WardenPlan` DOES NOT DECLARE IT — the type is the shape every verb answers
 * with, and only one of them mints anything — so it is read off the plan rather
 * than added to a type most plans would leave empty. It is already inside the
 * diff below; this is the copy somebody can select without reading a diff to
 * find it, which for a value whose whole purpose is to be pasted into a signup
 * form is the difference between a feature and a puzzle.
 */
const mintedIn = (plan: WardenPlan | null): string | null =>
  (plan as (WardenPlan & { readonly minted?: string }) | null)?.minted ?? null

interface Form {
  readonly verb: string
  readonly title: string
  readonly fields: readonly { name: string; label: string; placeholder?: string; optional?: boolean }[]
  readonly note?: string
}

/**
 * The forms, one per verb.
 *
 * THE VERB LIST IS THE WARDEN'S AND THIS IS A VIEW OF IT. A form here for a verb
 * the warden does not perform is a 400 somebody meets after filling it in; the
 * origin's `bad_verb` names what it does perform, which is what makes that
 * recoverable rather than mysterious.
 */
const FORMS: Readonly<Record<Tab, readonly Form[]>> = {
  accounts: [
    {
      verb: 'account.create',
      title: 'Add an account',
      fields: [
        { name: 'email', label: 'Address', placeholder: 'anna@h-group.net' },
        { name: 'password', label: 'Password', placeholder: 'leave empty and one is generated', optional: true },
      ],
      note: 'A generated password is shown once and never again.',
    },
    {
      verb: 'account.setPassword',
      title: 'Change a password',
      fields: [
        { name: 'email', label: 'Address' },
        { name: 'password', label: 'New password', placeholder: 'leave empty and one is generated', optional: true },
      ],
    },
    {
      verb: 'account.setQuota',
      title: 'Set a quota',
      fields: [
        { name: 'email', label: 'Address' },
        { name: 'quota', label: 'Quota', placeholder: '5G' },
      ],
    },
    {
      verb: 'account.delete',
      title: 'Remove an account',
      fields: [{ name: 'email', label: 'Address' }],
      note: 'Takes the account, its quota and every alias pointing at it. The mail on disk is left alone.',
    },
  ],
  aliases: [
    {
      verb: 'alias.create',
      title: 'Add or change an alias',
      fields: [
        { name: 'source', label: 'Address', placeholder: 'family@h-group.net' },
        { name: 'destination', label: 'Delivers to', placeholder: 'dh@h-group.net' },
      ],
    },
    { verb: 'alias.delete', title: 'Remove an alias', fields: [{ name: 'source', label: 'Address' }] },
  ],
  /**
   * Disposable addresses, which are separate from aliases on purpose.
   *
   * THEY ARE THE SAME MECHANISM AND NOT THE SAME ACT. A named alias is a route
   * into the house that several people rely on and that is meant to outlive
   * whoever made it; a disposable address is one you hand to a shop and burn
   * when it leaks. Putting them under one heading would make "remove an alias"
   * and "revoke this" the same button, and only one of those is safe to press
   * quickly.
   */
  disposable: [
    {
      verb: 'masked.create',
      title: 'Mint a disposable address',
      fields: [
        { name: 'owner', label: 'Delivers to', placeholder: 'dh@h-group.net' },
        { name: 'site', label: 'For which site', placeholder: 'petco.com', optional: true },
      ],
      note:
        'The address is generated, and the site is only a hint inside it so you can tell them apart later. ' +
        'Replies go straight from the address itself — no forwarding address, no ra_… gibberish in the ' +
        'recipient’s inbox — because this is your mail server.',
    },
    {
      verb: 'masked.revoke',
      title: 'Revoke one',
      fields: [{ name: 'address', label: 'Address' }],
      note:
        'Mail sent there afterwards is refused at the door, and the sender’s own server tells them so. ' +
        'It is never silently dropped: a signup that still needs this address should fail visibly rather ' +
        'than leave somebody waiting for an email that was accepted and deleted.',
    },
  ],
  // THE CONTAINER VERBS ARE NOT FORMS OF THIS KIND: they have no plan and no
  // diff, so `ServerControls` draws them, and this list is empty on purpose.
  maintenance: [],
  audit: [],
}

/**
 * What a refusal from the warden says to the person who caused it.
 *
 * `retryable` IS READ HERE, and it is the WARDEN'S field rather than the
 * `recoverable` on `HearthError`. mailgw forwards these bodies verbatim on
 * purpose — rewriting them there would mean two files deciding what "the tree
 * moved" means — so `recoverable` is absent from every warden refusal and
 * `HearthError` defaults it to false. This surface is the only consumer the
 * field has, and until it read it, `config_locked` and `tree_moved` arrived
 * indistinguishable from "what you typed is wrong": the one thing they are not.
 *
 * THE TWO WORDS ARE NOT SYNONYMS, which is why this reads the warden's rather
 * than translating it into mailgw's on the way past. `recoverable` asks whether
 * the CLIENT can put itself right — mailgw answers it `false` for every upstream
 * that simply did not respond — and `retryable` means "ask for this again,
 * unchanged, and it may work", which is exactly what a lock DMS is holding is.
 *
 * A REFUSAL NAMES ITS FIELDS. The warden hands back one entry per rejected field
 * so a form can put each beside its input; showing only the first would send
 * somebody round the loop once per mistake.
 */
function refusalText(error: unknown): string {
  if (!(error instanceof HearthError)) {
    return error instanceof Error ? error.message : 'That did not go through'
  }
  const body = error.body as { refusals?: { field: string; reason: string }[]; retryable?: boolean } | null
  const said = body?.refusals?.map((r) => `${r.field}: ${r.reason}`).join(' · ') ?? error.message
  return body?.retryable === true ? `${said} You can ask for it again.` : said
}

export function Admin({ open, onClose, onError, capabilities }: AdminProps): React.JSX.Element | null {
  const tabs = (['accounts', 'aliases', 'disposable', 'maintenance', 'audit'] as const).filter(
    (name) => !capabilities || capabilities.has(TAB_CAPABILITY[name]),
  )
  /**
   * THE TAB IS CHOSEN, NOT STORED, and the difference is the whole of this bug.
   *
   * It was `useState<Tab>(tabs[0] ?? 'accounts')`, which reads right and is a
   * lie about WHEN: an initialiser runs once, at mount, and `app.tsx` renders
   * this panel unconditionally — `if (!open) return null` is below every hook —
   * so it ran while `/whoami` was still in flight and `capabilities` was
   * undefined. Every tab was therefore on offer at that instant, `accounts` won,
   * and the answer was frozen before the question could be asked. Somebody
   * holding only `admin.logs` still landed on an Accounts tab whose every form
   * answers 403, which is exactly what the gate was added to stop; and every
   * test passed, because a test hands the capabilities in at the first render
   * and the shell cannot.
   *
   * DERIVED FROM `tabs` ON EVERY RENDER, so the choice follows the answer
   * whenever it arrives. `chosen` is what the reader picked, kept only while it
   * is still a tab they have — a capability that goes away must not leave the
   * panel showing a screen behind it.
   */
  const [chosen, setTab] = useState<Tab | null>(null)
  const tab: Tab = chosen && tabs.includes(chosen) ? chosen : (tabs[0] ?? 'accounts')
  const [values, setValues] = useState<Record<string, string>>({})
  const [plan, setPlan] = useState<WardenPlan | null>(null)
  const [applying, setApplying] = useState(false)
  const [applied, setApplied] = useState<string | null>(null)
  const [audit, setAudit] = useState<readonly AuditRow[] | null>(null)
  /** The last few hundred lines the origin said. The third thing `admin.logs` opens. */
  const [logs, setLogs] = useState<readonly { at: number; text: string }[]>([])
  /** What the ingest refused to store, and why. The other half of `admin.logs`. */
  const [skipped, setSkipped] = useState<
    readonly { guid: string; mailbox: string | null; uid: number | null; reason: string | null; ts: number | null }[]
  >([])
  /**
   * What the warden's `/health` said, kept as it said it.
   *
   * `ctl` IS OPTIONAL HERE FOR `maskedDomain`'S REASON. An origin that has not
   * learned to answer it must leave the Maintenance controls alone rather than
   * hide them, so `undefined` means "did not say", and only an explicit `false`
   * means "there is no pipe into the mail container". It is passed down on every
   * render rather than copied into `ServerControls`, which mounts before this
   * answer arrives.
   */
  const [health, setHealth] = useState<{
    configLocked: boolean
    maskedDomain?: string | null
    ctl?: boolean
  } | null>(null)
  /**
   * The address the last applied mint created, kept AFTER the diff is dismissed.
   *
   * IT OUTLIVES THE PLAN BECAUSE THAT IS WHEN IT BECOMES REAL. Applying closes
   * the review modal, and until this was held here the one thing somebody came
   * to this tab for went with it — leaving a person to find their new address by
   * reading the audit log. Held rather than parsed back out of the summary,
   * because the summary's wording is the warden's and a second reader of it here
   * would be one edit away from showing the wrong half of it.
   */
  const [minted, setMinted] = useState<string | null>(null)
  /**
   * What was asked for, so it can be asked for AGAIN once the second factor is
   * in hand.
   *
   * `WardenPlan` carries the verb and not the arguments, and re-planning needs
   * both. Held here rather than reconstructed from `values`, because `revert` is
   * a proposal too and its argument — a commit hash — was never in a form.
   */
  const [proposal, setProposal] = useState<{ verb: string; args: Record<string, unknown> } | null>(null)
  /** Unix SECONDS, from the origin. The API's clock, not this browser's. */
  const [reauthedUntil, setReauthedUntil] = useState<number | null>(null)

  /**
   * Put the diff away, and everything that only made sense beside it.
   *
   * THE GRANT IS NOT REVOKED BY THIS — it lives on the origin for its fifteen
   * minutes whatever this panel does — but the sentence saying when it expires
   * belongs to the change it was read against, and leaving it above the next
   * diff would be a claim about a change nobody has looked at yet. A half-pasted
   * token goes with the modal: it lives in `ReauthForm`, which unmounts with it.
   */
  const dismissPlan = useCallback((): void => {
    setPlan(null)
    setProposal(null)
    setReauthedUntil(null)
  }, [])

  const refreshAudit = useCallback(() => {
    api
      .wardenAudit()
      .then((r) => setAudit(r.entries))
      .catch(() => setAudit([]))
    /**
     * AND THE MESSAGES THE INGEST REFUSED, which is the other log this
     * capability gates and had no reader at all.
     *
     * `/admin/health` has always reported a COUNT of them and the route that
     * names them has always existed; nothing called it, so the only way to find
     * out WHICH messages were refused, and why, was `curl` over the tailnet.
     * They are on this tab rather than a fourth one because `admin.logs` is one
     * grant and this is the second half of what it opens.
     *
     * A FAILURE LEAVES IT EMPTY AND SAYS NOTHING. The audit trail beside it is
     * what this tab is mostly for; two error sentences about one dead origin
     * read as two faults.
     */
    api
      .ingestSkipped()
      .then((r) => setSkipped(Array.isArray(r.skipped) ? r.skipped : []))
      .catch(() => setSkipped([]))
    /**
     * AND WHAT THE ORIGIN ITSELF HAS SAID, which is the third thing this
     * capability opens and the one an operator actually reaches for.
     *
     * A 503 IS AN ANSWER AND NOT A FAILURE: a build that keeps no ring says so,
     * and `[]` would show "nothing has happened" about a running server. The
     * catch leaves the list empty and the section absent, which is the same
     * thing said by not saying it.
     */
    api
      .originLogs()
      .then((r) => setLogs(Array.isArray(r.lines) ? r.lines : []))
      .catch(() => setLogs([]))
  }, [])

  /**
   * Ask the warden how it is.
   *
   * A CALLBACK AS WELL AS AN EFFECT, because one answer can go stale while
   * somebody is looking at it. A container verb refused with "this installation
   * has no connection into the mail container" says the pipe went away under
   * this panel, and the Maintenance tab asks for a fresh answer rather than
   * deciding for itself what the new one would be.
   */
  const refreshHealth = useCallback((): void => {
    api
      .wardenHealth()
      // READ OFF THE ANSWER RATHER THAN OFF THE TYPE, like `revertable` on an
      // audit row and for the same reason: an origin that has not learned to say
      // a field yet must leave the surface alone, not hide it. `undefined` means
      // "did not say"; only an explicit `null` means this installation has no
      // disposable subdomain, and only an explicit `false` means it has no pipe
      // into the mail container.
      .then((h) => {
        const said = h as { maskedDomain?: string | null; ctl?: unknown }
        setHealth({
          configLocked: h.configLocked,
          maskedDomain: said.maskedDomain,
          ...(typeof said.ctl === 'boolean' ? { ctl: said.ctl } : {}),
        })
      })
      .catch(() => setHealth(null))
  }, [])

  useEffect(() => {
    if (!open) return
    refreshHealth()
  }, [open, refreshHealth])

  useEffect(() => {
    if (!open || tab !== 'audit' || audit !== null) return
    refreshAudit()
  }, [open, tab, audit, refreshAudit])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return
      // ESCAPE CLOSES THE PLAN FIRST. Two layers and one key: dismissing the
      // whole panel out from under a diff somebody is reading would leave them
      // unsure whether it had been applied.
      if (plan) dismissPlan()
      else onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose, plan, dismissPlan])

  /**
   * Ask the warden what a change would do.
   *
   * EVERY PROPOSAL COMES THROUGH HERE — the forms, an undo, and the re-ask after
   * a re-authentication — so `proposal` is never a stale record of a different
   * question from the one `plan` answers. Two of those three would otherwise
   * have had to remember to write it down.
   */
  const ask = useCallback(
    (verb: string, args: Record<string, unknown>): Promise<void> => {
      setApplied(null)
      // A NEW QUESTION CLEARS THE OLD ANSWER. Leaving the previous address on
      // screen beside a fresh diff would be an address somebody copies believing
      // it is the one they just asked for.
      setMinted(null)
      setProposal({ verb, args })
      return api
        .wardenPlan(verb, args)
        .then(setPlan)
        .catch((error: unknown) => onError(refusalText(error)))
    },
    [onError],
  )

  /**
   * Undo one entry in the log.
   *
   * IT IS A VERB LIKE ANY OTHER, so this drops into the same plan modal as every
   * form on the other tabs: the diff of the undo IS the confirmation, and a
   * second dialog asking "are you sure" would be a claim standing in front of
   * the evidence. The warden decides what an undo means — one action is several
   * commits, and it puts back every file that action touched.
   */
  const revert = (commit: string): void => {
    void ask('change.revert', { commit })
  }

  const propose = (form: Form): void => {
    const args: Record<string, unknown> = {}
    for (const field of form.fields) {
      const value = (values[`${form.verb}.${field.name}`] ?? '').trim()
      if (value !== '') args[field.name] = value
    }
    void ask(form.verb, args)
  }

  /**
   * The grant landed. Ask the same question again.
   *
   * SPENDING THE TOKEN IS `ReauthForm`'S JOB and this is the half that was
   * always the interesting one: what to do once the origin has agreed.
   *
   * THE SECOND HALF IS THE POINT, and doing it any other way is how this ends up
   * lying. The obvious shape — record "they re-authenticated" here and light the
   * Apply button on it — puts a second, local opinion beside the warden's
   * `needsReauth`, and the two disagree the moment anything is off: a token that
   * verified but named a different account, a grant made against a warden that
   * is not the one this panel talks to, a clock that is not what either side
   * thinks. The warden decides whether a change may be applied, so the honest
   * question is the one it already answers — so the plan is asked for again and
   * `needsReauth` comes back as the answer. If the grant did not take, the form
   * is simply still there, which is the truth.
   *
   * A FRESH PLAN IS ALSO THE ONLY USABLE ONE. A plan token expires five minutes
   * after it is minted, and signing in through an identity provider takes longer
   * than that often enough to matter — so the plan that was on screen while the
   * administrator was in the other tab is very often dead. Re-asking replaces it
   * with a diff of the tree as it is NOW, which is the diff they should be
   * approving anyway.
   */
  const signedInAgain = (expiresAt: number): Promise<void> => {
    // THE ORIGIN'S OWN NUMBER, rather than fifteen minutes counted from this
    // browser's clock. The two are not the same, and the one that decides is the
    // one that answered.
    setReauthedUntil(expiresAt)
    // `ask` REPORTS ITS OWN FAILURES, so there is nothing to catch here: if the
    // re-ask is refused, the reader is told and the form is simply still there.
    return proposal ? ask(proposal.verb, proposal.args) : Promise.resolve()
  }

  const apply = (): void => {
    if (!plan) return
    setApplying(true)
    api
      .wardenApply(plan.token)
      .then(() => {
        setApplied(plan.summary)
        // READ BEFORE `dismissPlan` THROWS THE PLAN AWAY, and only now: an
        // address in a plan is one nothing has written yet.
        setMinted(mintedIn(plan))
        dismissPlan()
        setValues({})
        // THE LOG IS RE-READ RATHER THAN APPENDED TO. It is the git history and
        // not a list this component maintains, so guessing what landed in it
        // would be inventing an audit entry.
        setAudit(null)
        if (tab === 'audit') refreshAudit()
      })
      // THE SAME READING AS A PLAN'S REFUSAL. An apply meets `tree_moved` and
      // `config_locked` more often than a plan does — the tree can move between
      // reading the diff and pressing the button — and those are precisely the
      // ones worth telling somebody they may simply ask for again.
      .catch((error: unknown) => onError(refusalText(error)))
      .finally(() => setApplying(false))
  }

  if (!open) return null

  return (
    <div
      className="settings-scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !plan) onClose()
      }}
    >
      <div className="settings-panel admin-panel" role="dialog" aria-modal="true" aria-label="Server">
        <div className="settings-head">
          <h2 className="settings-title">Server</h2>
          <button className="settings-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {/* SAID, NOT HIDDEN. A form that submits into a locked configuration is
            a refusal somebody meets after filling it in.

            `/health`'s `ctl` IS SAID ON THE MAINTENANCE TAB, beside the four
            controls it is about, rather than here. "No dms-ctl" is a working
            state and not a fault, and a warning standing above every tab would
            name controls most of them do not have — so it belongs where the
            verbs that need that pipe are, which is where they now are. */}
        {health?.configLocked ? (
          <p className="admin-warning">
            docker-mailserver is reading its configuration right now. A change made in the next minute will be
            refused — try again in a moment.
          </p>
        ) : null}

        <div className="admin-tabs" role="tablist" aria-label="Sections">
          {tabs.map((name) => (
            <button
              key={name}
              role="tab"
              aria-selected={tab === name}
              className="admin-tab"
              onClick={() => setTab(name)}
            >
              {name[0]!.toUpperCase() + name.slice(1)}
            </button>
          ))}
        </div>

        {applied ? (
          <p className="admin-applied" role="status">
            {applied} — done. It is in the log below.
          </p>
        ) : null}

        {/* THE ADDRESS, AFTER IT IS REAL. `.admin-secret` is borrowed rather
            than a new class of its own: it is the panel's "here is a value, take
            it now" box, monospaced and set apart, and this is the same act as
            handing over a generated password minus the part about never being
            able to see it again. This one is in the log, and saying so is what
            keeps somebody from treating the box as their only copy. */}
        {minted ? (
          <p className="admin-secret" role="status">
            <code>{minted}</code> is live. Paste it into the signup. Replies go straight from it, and if it ever
            starts receiving mail from somebody else, revoke it here — the log keeps the address either way.{' '}
            <button
              type="button"
              className="admin-propose"
              onClick={() => {
                // OPTIONAL-CHAINED because the clipboard API is absent over
                // plain HTTP and in some embedded webviews, and a copy button
                // that throws would take the whole panel down with it. The
                // address is selectable text either way, which is the fallback.
                void navigator.clipboard?.writeText(minted)
              }}
            >
              Copy
            </button>
          </p>
        ) : null}

        {tab === 'maintenance' ? (
          /* THE FOUR THAT RUN INSIDE THE MAIL CONTAINER. `ctl` is handed down on
             every render rather than copied into that component: `/health`
             answers after this panel mounts, so a copy taken once is a copy of
             "not yet". `refreshHealth` is how it asks again when the warden says
             the pipe has gone. */
          <ServerControls ctl={health?.ctl} refreshHealth={refreshHealth} onError={onError} />
        ) : tab === 'audit' ? (
          <div className="admin-audit">
            {/* THE MESSAGES THE INDEX REFUSED, above the change log because they
                are the ones nobody has seen. A count on the health panel with no
                way to reach the list is a number that leads nowhere, and this
                was that number for as long as the route has existed. */}
            {/* WHAT THE PROCESS HAS SAID, first, because it is the one an
                operator opens this tab for. Folded, because it is five hundred
                lines and the change log below it is the thing a household
                reads. */}
            {logs.length > 0 ? (
              <details className="admin-logs">
                <summary>{`What the server has said (${logs.length.toLocaleString()} line${logs.length === 1 ? '' : 's'})`}</summary>
                <ul className="admin-audit-list">
                  {logs.map((line, index) => (
                    <li key={`${line.at}-${index}`}>
                      <span className="admin-audit-when">{new Date(line.at * 1000).toLocaleTimeString()}</span>
                      <span className="admin-audit-what">{line.text}</span>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
            {skipped.length > 0 ? (
              <details className="admin-skipped">
                <summary>{`${skipped.length.toLocaleString()} message${skipped.length === 1 ? '' : 's'} the index refused`}</summary>
                <ul className="admin-audit-list">
                  {skipped.map((one) => (
                    <li key={one.guid}>
                      <span className="admin-audit-when">
                        {one.mailbox ?? '—'}
                        {one.uid === null ? '' : `/${one.uid}`}
                      </span>
                      <span className="admin-audit-what">{one.reason ?? 'no reason recorded'}</span>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
            {audit === null ? (
              <p className="screener-empty">…</p>
            ) : audit.length === 0 ? (
              <p className="screener-empty">Nothing has been changed.</p>
            ) : (
              <ul className="admin-audit-list">
                {audit.map((entry) => (
                  <li key={entry.commit}>
                    <span className="admin-audit-when">{new Date(entry.at * 1000).toLocaleString()}</span>
                    <span className="admin-audit-what">{entry.message}</span>
                    {/* OFFERED ONLY WHERE THE ORIGIN SAYS IT WOULD WORK. The
                        log holds the configuration as the warden first found it,
                        which is a state and not an action — and a button that
                        refuses when pressed is worse than one that is not there.
                        Which rows those are is the warden's answer, not a rule
                        repeated here. */}
                    {entry.revertable ? (
                      <button type="button" className="admin-propose" onClick={() => revert(entry.commit)}>
                        Undo this change
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : tab === 'disposable' && health?.maskedDomain === null ? (
          // SAID INSTEAD OF THE FORMS, not beside them. The warden refuses both
          // verbs outright without a subdomain, so offering the form here would
          // be offering a control whose only possible outcome is a refusal
          // naming an environment variable — after somebody has filled it in.
          <p className="admin-warning">
            This server has no disposable-address subdomain, so there is nothing to mint on. The subdomain is what
            keeps a leaked address from exposing your real domain, so it is set up once, deliberately: give the
            warden <code>WARDEN_MASKED_DOMAIN</code> and list the same domain in <code>WARDEN_DOMAINS</code>.
          </p>
        ) : (
          <div className="admin-forms">
            {tab === 'disposable' && health?.maskedDomain ? (
              <p className="admin-note">
                Addresses are minted on <code>{health.maskedDomain}</code>.
              </p>
            ) : null}
            {FORMS[tab].map((form) => (
              <form
                key={form.verb}
                className="admin-form"
                onSubmit={(event) => {
                  event.preventDefault()
                  propose(form)
                }}
              >
                <h3 className="admin-form-title">{form.title}</h3>
                {form.fields.map((field) => (
                  <label key={field.name} className="admin-field">
                    <span>{field.label}</span>
                    <input
                      value={values[`${form.verb}.${field.name}`] ?? ''}
                      placeholder={field.placeholder ?? ''}
                      spellCheck={false}
                      autoComplete="off"
                      onChange={(event) =>
                        setValues((current) => ({ ...current, [`${form.verb}.${field.name}`]: event.target.value }))
                      }
                    />
                  </label>
                ))}
                {form.note ? <p className="admin-note">{form.note}</p> : null}
                {/* "Show the change" AND NOT "Save". The button does what it
                    says: nothing is written until the diff has been read. */}
                <button type="submit" className="admin-propose">
                  Show the change
                </button>
              </form>
            ))}
          </div>
        )}
      </div>

      {plan ? (
        <div className="settings-scrim admin-plan-scrim">
          <div className="settings-panel admin-plan" role="alertdialog" aria-modal="true" aria-label="Review the change">
            <h3 className="admin-plan-title">{plan.summary}</h3>

            {plan.destructive ? (
              <p className="admin-warning">{plan.because ?? 'This cannot be undone from the app.'}</p>
            ) : null}

            {/* HANDED OVER ONCE. It is not in the diff — that carries the hash —
                and it is not stored anywhere it can be read again. */}
            {plan.secret ? (
              <p className="admin-secret">
                The password is <code>{plan.secret}</code>. Write it down now: it is not saved anywhere and cannot
                be shown again.
              </p>
            ) : null}

            {/* WHAT THIS WOULD CREATE, ABOVE THE DIFF THAT PROVES IT. The
                address is in the diff either way — the diff is still the
                product — and this is the line that saves reading one to answer
                "which address am I about to be given". It says "would" on
                purpose: nothing is written until the button below. */}
            {mintedIn(plan) ? (
              <p className="admin-note">
                This would create <code>{mintedIn(plan)}</code>.
              </p>
            ) : null}

            {plan.diffs.map((entry) => (
              <div key={entry.file} className="admin-diff">
                <div className="admin-diff-file">{entry.file}</div>
                <pre>
                  {entry.diff
                    .split('\n')
                    .filter((line) => !/^(diff |index |--- |\+\+\+ )/.test(line))
                    .join('\n')
                    .trim() || 'No change.'}
                </pre>
              </div>
            ))}

            {/* THE SECOND FACTOR, WHERE IT IS SPENT. This used to be one
                sentence — "Sign in again at /admin/reauth first" — beside no
                control at all, which told the truth and then left the reader
                holding a URL, a token they could not paste anywhere, and a
                change nobody could make. The instruction and the input belong
                together, and everything about that ceremony is manual by
                design: see `apps/mailgw/src/auth/reauth.ts`. */}
            {plan.needsReauth ? (
              <ReauthForm
                because="This change cannot be undone."
                onGranted={signedInAgain}
                onRefused={(error) => onError(refusalText(error))}
              />
            ) : reauthedUntil !== null ? (
              // SAID ONCE THE WARDEN HAS AGREED, and it carries the origin's
              // number rather than fifteen minutes counted from this browser's
              // clock — the two are not the same, and the one that decides is
              // the one that answered.
              <p className="admin-note" role="status">
                Signed in again. This stands until {new Date(reauthedUntil * 1000).toLocaleTimeString()}.
              </p>
            ) : null}

            <div className="compose-actions">
              <button type="button" className="compose-cancel" onClick={dismissPlan}>
                Cancel
              </button>
              {/* NO APPLY BUTTON UNTIL THE WARDEN SAYS IT WOULD WORK. The form
                  above is the way forward while it would not, and `needsReauth`
                  is re-read from a fresh plan after the token is spent — this
                  side never decides it. */}
              {plan.needsReauth ? null : (
                <button type="button" className="admin-apply" disabled={applying} onClick={apply}>
                  {applying ? 'Applying…' : 'Apply this change'}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
